import React from 'react';
import { Card, CardHeader, CardBody, CardFooter, Button } from './ui';
import { StatusBadge, ApplicationStatus } from './StatusBadge';

export interface Candidate {
  id: number;
  srCandidateId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  location: string | null;
  source: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface Application {
  id: number;
  status: ApplicationStatus;
  jobTitle?: string;
  createdAt: Date | string;
}

export interface CandidateCardProps {
  candidate: Candidate;
  application?: Application;
  onView?: (candidate: Candidate) => void;
  onEmail?: (candidate: Candidate) => void;
  className?: string;
  compact?: boolean;
  showApplication?: boolean;
}

const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  application,
  onView,
  onEmail,
  className = '',
  compact = false,
  showApplication = true,
}) => {
  const fullName = `${candidate.firstName} ${candidate.lastName}`;
  const initials = getInitials(candidate.firstName, candidate.lastName);

  if (compact) {
    return (
      <div
        className={`
          flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200
          hover:border-gray-300 transition-colors
          ${onView ? 'cursor-pointer' : ''}
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        onClick={() => onView?.(candidate)}
        role={onView ? 'button' : undefined}
        tabIndex={onView ? 0 : undefined}
        onKeyDown={(e) => e.key === 'Enter' && onView?.(candidate)}
      >
        {/* Avatar */}
        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
          <span className="text-sm font-medium text-blue-700">{initials}</span>
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {fullName}
            </h3>
            {application && showApplication && (
              <StatusBadge status={application.status} size="sm" showDot={false} />
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{candidate.email}</p>
        </div>
      </div>
    );
  }

  return (
    <Card className={className} variant="default">
      <CardHeader>
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="flex-shrink-0 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <span className="text-lg font-medium text-blue-700">{initials}</span>
          </div>

          {/* Info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {fullName}
              </h3>
            </div>
            <p className="text-sm text-gray-500">{candidate.email}</p>
            {candidate.phone && (
              <p className="text-sm text-gray-500 mt-0.5">{candidate.phone}</p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardBody>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          {candidate.location && (
            <div>
              <dt className="text-gray-500">Location</dt>
              <dd className="font-medium text-gray-900 mt-0.5">
                {candidate.location}
              </dd>
            </div>
          )}
          {candidate.source && (
            <div>
              <dt className="text-gray-500">Source</dt>
              <dd className="font-medium text-gray-900 mt-0.5">
                {candidate.source}
              </dd>
            </div>
          )}
        </dl>

        {application && showApplication && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Application Status
                </p>
                {application.jobTitle && (
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {application.jobTitle}
                  </p>
                )}
              </div>
              <StatusBadge status={application.status} />
            </div>
          </div>
        )}

        <div className="mt-4 text-xs text-gray-500">
          ID: {candidate.srCandidateId}
        </div>
      </CardBody>

      {(onView || onEmail) && (
        <CardFooter>
          {onEmail && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEmail(candidate)}
              leftIcon={
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              }
            >
              Email
            </Button>
          )}
          {onView && (
            <Button variant="secondary" size="sm" onClick={() => onView(candidate)}>
              View Profile
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};

CandidateCard.displayName = 'CandidateCard';

// Simple list item variant for tables/lists
export interface CandidateListItemProps {
  candidate: Candidate;
  application?: Application;
  onClick?: (candidate: Candidate) => void;
  selected?: boolean;
  className?: string;
}

export const CandidateListItem: React.FC<CandidateListItemProps> = ({
  candidate,
  application,
  onClick,
  selected = false,
  className = '',
}) => {
  const fullName = `${candidate.firstName} ${candidate.lastName}`;
  const initials = getInitials(candidate.firstName, candidate.lastName);

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3
        ${onClick ? 'cursor-pointer hover:bg-gray-50' : ''}
        ${selected ? 'bg-blue-50' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      onClick={() => onClick?.(candidate)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.(candidate)}
    >
      {/* Avatar */}
      <div
        className={`
          flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center
          ${selected ? 'bg-blue-200' : 'bg-gray-100'}
        `}
      >
        <span
          className={`text-xs font-medium ${selected ? 'text-blue-800' : 'text-gray-600'}`}
        >
          {initials}
        </span>
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{fullName}</p>
        <p className="text-xs text-gray-500 truncate">{candidate.email}</p>
      </div>

      {/* Status */}
      {application && (
        <StatusBadge status={application.status} size="sm" showDot={false} />
      )}
    </div>
  );
};

CandidateListItem.displayName = 'CandidateListItem';
